import { getAccessToken } from '../auth/getAccessToken'

const defaultTimeoutMs = 15000
const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

export class ApiError extends Error {
  constructor (message, { data, method, status, statusText, url }) {
    super(message)
    this.name = 'ApiError'
    this.data = data
    this.method = method
    this.status = status
    this.statusText = statusText
    this.url = url
  }
}

function buildApiUrl (path, queryParams) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const url = /^https?:\/\//i.test(path) ? path : `${apiBaseUrl}${normalizedPath}`
  const searchParams = new URLSearchParams()

  for (const [name, value] of Object.entries(queryParams ?? {})) {
    const values = Array.isArray(value) ? value : [value]

    for (const item of values) {
      if (item) {
        searchParams.append(name, String(item))
      }
    }
  }

  const queryString = searchParams.toString()

  return queryString ? `${url}${url.includes('?') ? '&' : '?'}${queryString}` : url
}

function prepareRequestBody (body, headers) {
  if (!body) {
    return undefined
  }

  const isRawBody = typeof body === 'string' ||
    body instanceof Blob ||
    body instanceof FormData ||
    body instanceof URLSearchParams

  if (isRawBody) {
    return body
  }

  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  return JSON.stringify(body)
}

async function readErrorDataAsync (response) {
  const responseText = await response.text()

  if (!responseText) {
    return null
  }

  if (!response.headers.get('content-type')?.includes('json')) {
    return responseText
  }

  try {
    return JSON.parse(responseText)
  } catch (error) {
    console.error('The API returned an invalid JSON error response.', error)
    return responseText
  }
}

function getErrorMessage (data, response) {
  if (typeof data === 'string') {
    return data
  }

  return data?.error ||
    `${response.status} ${response.statusText}`.trim()
}

async function readResponseDataAsync (response, responseType) {
  if (responseType === 'response') {
    return response
  }

  if (response.status === 204) {
    return null
  }

  if (responseType === 'blob') {
    return response.blob()
  }

  const responseText = await response.text()

  if (!responseText) {
    return null
  }

  if (responseType === 'text') {
    return responseText
  }

  const responseData = JSON.parse(responseText)

  return responseData?.success === true && Object.hasOwn(responseData, 'data')
    ? responseData.data
    : responseData
}

export async function apiRequestAsync (path, options = {}) {
  const {
    body,
    headers: customHeaders = {},
    method = 'GET',
    queryParams,
    responseType = 'json',
    timeoutMs = defaultTimeoutMs,
  } = options

  const normalizedMethod = method.toUpperCase()
  const url = buildApiUrl(path, queryParams)
  const headers = new Headers(customHeaders)
  const isAbsoluteUrl = /^https?:\/\//i.test(path)
  const isApiRequest = !isAbsoluteUrl || (apiBaseUrl && path.startsWith(`${apiBaseUrl}/`))

  if (isApiRequest && !headers.has('Authorization')) {
    const accessToken = await getAccessToken()

    if (!accessToken) {
      return null
    }

    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  const controller = new AbortController()
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs)

  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json')
  }

  try {
    const response = await fetch(url, {
      body: prepareRequestBody(body, headers),
      headers,
      method: normalizedMethod,
      signal: controller.signal,
    })

    if (!response.ok) {
      const data = await readErrorDataAsync(response)

      throw new ApiError(getErrorMessage(data, response), {
        data,
        method: normalizedMethod,
        status: response.status,
        statusText: response.statusText,
        url,
      })
    }

    const data = await readResponseDataAsync(response, responseType)

    return data
  } finally {
    globalThis.clearTimeout(timeout)
  }
}

export const apiClient = {
  deleteAsync (path, options = {}) {
    return apiRequestAsync(path, { ...options, method: 'DELETE' })
  },
  getAsync (path, options = {}) {
    return apiRequestAsync(path, { ...options, method: 'GET' })
  },
  patchAsync (path, body, options = {}) {
    return apiRequestAsync(path, { ...options, body, method: 'PATCH' })
  },
  postAsync (path, body, options = {}) {
    return apiRequestAsync(path, { ...options, body, method: 'POST' })
  },
  putAsync (path, body, options = {}) {
    return apiRequestAsync(path, { ...options, body, method: 'PUT' })
  },
  requestAsync: apiRequestAsync,
}
